import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { LogoutOutlined, UserOutlined } from '@ant-design/icons'
import { App as AntdApp, Button, Dropdown, Tooltip, Typography, type MenuProps } from 'antd'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/shared/auth/useAuth'
import { useVisibleNav } from './useVisibleNav'

/**
 * Profil menyusi — headerdagi odam ikonkasi ostidagi dropdown.
 *
 * ⚠️ «KIM BO'LIB KIRGANMAN» BANDI KASSIRDA ODDIY QULAYLIK EMAS.
 * Do'kondagi telefon yoki kompyuter bir necha odam qo'lidan o'tadi, va
 * kirilgan hisob KIMNING depozitidan pul yechilishini belgilaydi
 * (ADR-029 §2). Noto'g'ri hisob ostida ishlagan kassir boshqa odamning
 * pulini sarflaydi — bu ekranga qaytarib bo'lmaydigan xato.
 *
 * ⚠️ Klaviatura va skrin-rider (`selfcare/ProfileMenu` bilan bir xil
 * qatlam): `aria-haspopup` + `aria-expanded` + `aria-controls`,
 * `autoFocus`, `Escape` yopadi va fokusni TUGMAGA QAYTARADI. antd
 * `Dropdown` (rc-dropdown) da `Escape` ishlovchisi ham, `aria-expanded`
 * ham YO'Q — "antd o'zi qiladi" deb tashlab ketilsa klaviatura bilan
 * ishlaydigan odam menyudan chiqa olmasdi.
 *
 * ⚠️ JURNALLAR (`nav.secondary`) FAQAT TELEFONDA shu yerda. Kompyuterda
 * ular yuqori qatordagi menyuda ko'rinib turadi va ikkinchi nusxa
 * «menyu takrorlanmaydi» qoidasini buzardi (`AppHeader` javadoc'i).
 * Telefonda esa boshqa yo'l yo'q: pastki tab-bar to'rt bandda qoladi.
 *
 * ⚠️ «KIRISHLARIM» (`nav.account`) esa HAR DOIM shu yerda —
 * qurilmadan qat'i nazar (egasi talabi 2026-08-25). U hisobning O'Z
 * xavfsizlik ma'lumoti, ya'ni joyi hisob menyusi; yuqori qatorda u
 * kundalik ish bandlari bilan bir qatorda turib ularni suyultirardi.
 */
export function ProfileMenu({ isMobile }: { isMobile: boolean }) {
  const { t } = useTranslation()
  const { user, signOut } = useAuth()
  const { modal } = AntdApp.useApp()
  const navigate = useNavigate()
  const nav = useVisibleNav()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuId = useId()
  const [open, setOpen] = useState(false)

  /*
    ⚠️ IKKI MANBA, BITTA RO'YXAT.

    `nav.account` («Kirishlarim») HAR DOIM shu yerda — egasi talabi
    2026-08-25. `nav.secondary` (jurnallar) esa FAQAT TELEFONDA:
    kompyuterda ular yuqori qatorda ko'rinib turadi va ikkinchi nusxa
    «menyu takrorlanmaydi» qoidasini buzardi (`AppHeader` javadoc'i).
  */
  const extras = [...(isMobile ? nav.secondary : []), ...nav.account]

  const focusTrigger = useCallback(() => {
    triggerRef.current?.focus()
  }, [])

  /*
    ⚠️ `capture: true` — menyu ichidagi antd komponentlari `Escape` ni
    o'zicha to'xtatib qo'ymasin. Ishlovchi FAQAT menyu ochiq bo'lganda
    osiladi, ya'ni yopiq holatda hech qanday global tugma ushlanmaydi.
  */
  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopPropagation()
      setOpen(false)
      focusTrigger()
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [open, focusTrigger])

  /**
   * ⚠️ CHIQISH TASDIQLANADI. Kassir kun davomida shu menyuni balansini
   * ko'rish uchun ochadi va tasodifan «Chiqish» ga tegib ketishi oson;
   * qaytish narxi esa mijoz navbatda turganda to'lanadi.
   */
  const confirmSignOut = () => {
    modal.confirm({
      title: t('auth.logoutConfirmTitle'),
      content: t('auth.logoutConfirmText'),
      okText: t('auth.logoutConfirmOk'),
      cancelText: t('common.cancel'),
      okButtonProps: { danger: true },
      onOk: () => signOut(),
      onCancel: focusTrigger,
    })
  }

  const items: MenuProps['items'] = [
    {
      key: 'identity',
      type: 'group',
      /*
        ⚠️ TASHKILOT NOMI HAM SHU YERDA. `MeResponse` da `displayName`
        YO'Q — faqat `username` va `tenant.name`. Ikkinchisi sherik
        uchun ortiqcha bezak emas: reseller bir necha tashkilotga a'zo
        bo'lishi mumkin (`CHOOSE_TENANT` bosqichi shuning uchun bor) va
        «qaysi tashkilotning pulini olayapman» degan savolga boshqa
        hech qayerda javob yo'q.
      */
      label: (
        <span style={{ display: 'block', lineHeight: 1.35, paddingBlock: 2 }}>
          <Typography.Text strong style={{ display: 'block', fontSize: 14 }}>
            {user?.username ?? ''}
          </Typography.Text>
          {user ? (
            <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
              {user.tenantName}
            </Typography.Text>
          ) : null}
        </span>
      ),
      children: [],
    },
    ...(extras.length > 0
      ? [
          { type: 'divider' as const, key: 'nav-separator' },
          ...extras.map((item) => ({
            key: item.key,
            icon: item.icon,
            label: t(item.labelKey),
          })),
        ]
      : []),
    { type: 'divider', key: 'separator' },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('auth.logout'),
      danger: true,
    },
  ]

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      trigger={['click']}
      autoFocus
      menu={{
        id: menuId,
        items,
        onClick: ({ key }) => {
          setOpen(false)
          if (key === 'logout') {
            confirmSignOut()
            return
          }
          const item = extras.find((n) => n.key === key)
          if (item) navigate(item.path)
        },
      }}
    >
      <Tooltip title={t('account.menu.label')}>
        <Button
          ref={triggerRef}
          type="text"
          icon={<UserOutlined />}
          aria-label={t('account.menu.label')}
          aria-haspopup="menu"
          aria-expanded={open}
          {...(open ? { 'aria-controls': menuId } : {})}
        />
      </Tooltip>
    </Dropdown>
  )
}
